import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SignatureCapture from '../SignatureCapture';
import { 
  FileText, CheckCircle, Clock, AlertCircle, Download, 
  User, Users, Edit3, X, ChevronDown, ChevronUp 
} from 'lucide-react';
import './PVSignatures.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Composant pour gérer les signatures du PV d'AG
 * Permet à chaque membre du bureau (Président, Secrétaire, Scrutateurs) de signer
 */
const PVSignatures = ({ assemblee, token, onSignComplete, userRole, userId }) => {
  const [bureau, setBureau] = useState(null);
  const [signatures, setSignatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signingRole, setSigningRole] = useState(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    loadSignatures();
  }, [assemblee?.id]);

  const loadSignatures = async () => {
    if (!assemblee?.id) return;
    
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/assemblees/${assemblee.id}/pv/signatures`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setBureau(response.data.bureau || {});
      setSignatures(response.data.signatures || {});
    } catch (err) {
      console.error('Erreur chargement signatures:', err);
      setError('Impossible de charger les signatures');
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async (signatureData) => {
    try {
      const response = await axios.post(
        `${API}/assemblees/${assemblee.id}/pv/sign`,
        {
          role: signingRole,
          signature: signatureData.signature,
          timestamp: signatureData.timestamp
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh signatures
      await loadSignatures();
      setSigningRole(null);
      
      if (onSignComplete) {
        onSignComplete(response.data);
      }
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Erreur lors de la signature');
    }
  };

  const handleDownloadPV = async () => {
    try {
      const response = await axios.get(
        `${API}/assemblees/${assemblee.id}/pv/download`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PV_AG_${assemblee.id}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Erreur lors du téléchargement du PV');
    }
  };

  const getBureauMember = (role) => {
    if (!bureau) return null;
    
    if (role === 'president') return bureau.president;
    if (role === 'secretaire') return bureau.secretaire;
    if (role === 'scrutateur') {
      return bureau.scrutateurs?.[0] || null;
    }
    return null;
  };

  const canUserSign = (role) => {
    const member = getBureauMember(role);
    if (!member) return false;
    
    // Check if user is this bureau member
    return member.id === userId || userRole === 'gestionnaire' || userRole === 'admin';
  };

  const isAllSigned = () => {
    const requiredRoles = ['president', 'secretaire'];
    if (bureau?.scrutateurs?.length > 0) {
      requiredRoles.push('scrutateur');
    }
    return requiredRoles.every(role => signatures[role]?.signed);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="pv-signatures-loading">
        <Clock className="animate-spin" size={24} />
        <span>Chargement des signatures...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pv-signatures-error">
        <AlertCircle size={20} />
        <span>{error}</span>
      </div>
    );
  }

  // Only show if AG is closed
  if (assemblee?.status !== 'Passée' && assemblee?.status !== 'Clôturée') {
    return null;
  }

  const signatureRoles = [
    { 
      key: 'president', 
      label: 'Président de séance', 
      icon: User,
      member: bureau?.president 
    },
    { 
      key: 'secretaire', 
      label: 'Secrétaire', 
      icon: Edit3,
      member: bureau?.secretaire 
    },
    { 
      key: 'scrutateur', 
      label: 'Scrutateur', 
      icon: Users,
      member: bureau?.scrutateurs?.[0] 
    }
  ].filter(r => r.member);

  return (
    <div className="pv-signatures" data-testid="pv-signatures">
      {/* Header */}
      <div 
        className="pv-signatures-header"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="pv-signatures-title">
          <FileText className="text-[#00D3A8]" size={22} />
          <div>
            <h3>Signatures du Procès-Verbal</h3>
            <p className="pv-signatures-subtitle">
              {isAllSigned() 
                ? 'Toutes les signatures ont été collectées' 
                : 'En attente de signatures du bureau'}
            </p>
          </div>
        </div>
        <div className="pv-signatures-header-right">
          {isAllSigned() && (
            <span className="pv-complete-badge">
              <CheckCircle size={16} />
              Complet
            </span>
          )}
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>

      {expanded && (
        <div className="pv-signatures-content">
          {/* Signature slots */}
          <div className="pv-signature-slots">
            {signatureRoles.map(({ key, label, icon: Icon, member }) => (
              <div 
                key={key} 
                className={`pv-signature-slot ${signatures[key]?.signed ? 'signed' : ''}`}
                data-testid={`signature-slot-${key}`}
              >
                <div className="slot-header">
                  <div className="slot-role">
                    <Icon size={18} />
                    <span>{label}</span>
                  </div>
                  {signatures[key]?.signed ? (
                    <span className="slot-status signed">
                      <CheckCircle size={14} />
                      Signé
                    </span>
                  ) : (
                    <span className="slot-status pending">
                      <Clock size={14} />
                      En attente
                    </span>
                  )}
                </div>

                <div className="slot-member">
                  <span className="member-name">
                    {member?.prenom} {member?.nom}
                  </span>
                </div>

                {signatures[key]?.signed ? (
                  <div className="slot-signed-info">
                    <div className="signature-preview">
                      <img 
                        src={signatures[key].signature} 
                        alt={`Signature ${label}`}
                        className="signature-image"
                      />
                    </div>
                    <div className="signature-timestamp">
                      <Clock size={12} />
                      <span>{formatTimestamp(signatures[key].timestamp)}</span>
                    </div>
                  </div>
                ) : (
                  canUserSign(key) && (
                    <button
                      onClick={() => setSigningRole(key)}
                      className="slot-sign-btn"
                      data-testid={`sign-btn-${key}`}
                    >
                      <Edit3 size={16} />
                      Signer maintenant
                    </button>
                  )
                )}
              </div>
            ))}
          </div>

          {/* Download button */}
          <div className="pv-download-section">
            <button 
              onClick={handleDownloadPV}
              className="pv-download-btn"
              data-testid="download-pv-btn"
            >
              <Download size={18} />
              <span>Télécharger le PV {isAllSigned() ? '(signé)' : '(brouillon)'}</span>
            </button>
            {!isAllSigned() && (
              <p className="pv-download-note">
                Le PV final sera disponible une fois toutes les signatures collectées.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Signature Modal */}
      {signingRole && (
        <div className="signature-modal-overlay" data-testid="signature-modal">
          <div className="signature-modal">
            <SignatureCapture
              onSign={handleSign}
              onCancel={() => setSigningRole(null)}
              signerName={`${getBureauMember(signingRole)?.prenom || ''} ${getBureauMember(signingRole)?.nom || ''}`}
              signerRole={
                signingRole === 'president' ? 'Président de séance' :
                signingRole === 'secretaire' ? 'Secrétaire' :
                'Scrutateur'
              }
              title="Signature du Procès-Verbal"
              subtitle={`AG du ${new Date(assemblee.date).toLocaleDateString('fr-FR')}`}
              showTimestamp={true}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PVSignatures;
